import React from 'react';
import { Avatar, Icon, Popover, PriorityChip, StatusChip, fmtDate, getCategory, getRequester, getUser } from './components.jsx';
import { ALL_COLUMNS, BUILTIN_VIEWS, CATEGORIES, KB_ARTICLES, LOCATIONS, ME_ID, PRIORITIES, STATUSES, SUBCATS, USERS } from './data.jsx';
// Reused by the compliance ticket layout below to render the WSM audit
// panel identical to the User Requests admin detail view.
import { WsmApplyDetail } from './requests.jsx';
// Tickets - SysAid-style column table + full-page detail

// ⚠️ SECURITY: This decodes HTML entities back into raw HTML characters.
// Today that's safe because all callers render the result via React text
// children ({decodeEntities(...)}) which re-escapes everything before it
// reaches the DOM. DO NOT pair this with dangerouslySetInnerHTML - email
// bodies (the main caller) are untrusted; anyone on the internet can email
// the helpdesk mailbox. If you need rich HTML rendering, swap this for
// DOMPurify.sanitize() and only THEN use dangerouslySetInnerHTML.
const decodeEntities = (str) => (str || '')
  .replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
  // Numeric HTML entities (decimal and hex). Outlook/Gmail commonly encode
  // emojis this way (&#129318; = 🤦). Use fromCodePoint, NOT fromCharCode,
  // so supplementary-plane code points (most emoji) render correctly.
  // Still safe per the security note above because callers pass the result
  // through React text children, which re-escape before reaching the DOM.
  .replace(/&#(\d+);/g, (_, d) => { try { return String.fromCodePoint(parseInt(d, 10)); } catch { return ''; } })
  .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => { try { return String.fromCodePoint(parseInt(h, 16)); } catch { return ''; } })
  .replace(/[ \t]{2,}/g, ' ').trim();

// Render a single cell for the given column id
const renderCell = (col, ticket, onClick) => {
  switch (col) {
    case "id":        return <div className="tk-td col-id" onClick={onClick}>#{ticket.id}</div>;
    case "status":    return <div className="tk-td" onClick={onClick}><StatusChip status={ticket.status}/></div>;
    case "priority":  return <div className="tk-td" onClick={onClick}><PriorityChip priority={ticket.priority}/></div>;
    case "subject": {
      const threadCount = (ticket.messages || []).filter(m => m.from !== 'system').length;
      const attachmentCount = ticket.attachmentCount || 0;
      return (
        <div className="tk-td col-subject" onClick={onClick}>
          {ticket.subject}
          {ticket.duplicateOf && (
            <span style={{marginLeft:6, fontSize:10, fontWeight:500, color:"oklch(50% 0.10 70)", padding:"1px 5px", background:"oklch(96% 0.04 85)", borderRadius:3}}>MERGED</span>
          )}
          {threadCount > 1 && (
            <span style={{marginLeft:6, fontSize:9, fontWeight:700, letterSpacing:"0.04em", color:"oklch(40% 0.12 264)", padding:"2px 6px", background:"oklch(94% 0.04 264)", borderRadius:3}}>
              +{threadCount - 1} REPLIES
            </span>
          )}
          {/* Internal-note marker - signals an IT-only note has been added
              to the ticket (not part of the email thread the requester
              sees). hasInternalNote comes from the list query's existing
              message fetch, no extra round-trip. Amber to match the
              internal-note styling used in the detail view. */}
          {ticket.hasInternalNote && (
            <span title="Has an internal note"
                  style={{
                    marginLeft: 6, display:"inline-flex", alignItems:"center",
                    color: "oklch(55% 0.12 75)", verticalAlign: "1px",
                  }}>
              <Icon name="messageSquare" size={12}/>
            </span>
          )}
          {/* Paperclip badge - quick visual signal that the ticket has files.
              Count rendered alongside so users can scan for "ticket with the
              big attachment haul". The count comes from a single aggregate
              SQL query in GET /tickets - no per-row N+1. */}
          {attachmentCount > 0 && (
            <span title={`${attachmentCount} attachment${attachmentCount > 1 ? 's' : ''}`}
                  style={{
                    marginLeft: 6, display:"inline-flex", alignItems:"center", gap:3,
                    fontSize: 10, fontWeight: 600, color: "var(--text-subtle)",
                    padding: "1px 6px 1px 4px", background: "var(--bg-sub)",
                    borderRadius: 3, verticalAlign: "1px",
                  }}>
              <Icon name="paperclip" size={10}/>{attachmentCount}
            </span>
          )}
        </div>
      );
    }
    case "description": {
      const desc = ticket.messages[0]?.body || "";
      return <div className="tk-td col-desc" onClick={onClick} title={desc}>{desc}</div>;
    }
    case "category":  return <div className="tk-td col-cat" onClick={onClick}>{getCategory(ticket.category)?.label}</div>;
    case "subcat":    return <div className="tk-td col-cat" onClick={onClick}>{ticket.subcat}</div>;
    case "location":  return <div className="tk-td col-cat" onClick={onClick}>{ticket.location || ""}</div>;
    case "requester": {
      const r = getRequester(ticket.requester);
      return <div className="tk-td col-requester" onClick={onClick}><Avatar user={r} size="sm"/><span style={{overflow:"hidden",textOverflow:"ellipsis"}}>{r?.name}</span></div>;
    }
    case "assignee": {
      const a = ticket.assignee ? getUser(ticket.assignee) : null;
      return (
        <div className={`tk-td col-assignee ${a ? "" : "unassigned"}`} onClick={onClick}>
          {a ? <><Avatar user={a} size="sm"/><span>{a.name}</span></> : <span>- unassigned -</span>}
        </div>
      );
    }
    case "requestTime": return <div className="tk-td col-updated" onClick={onClick}>{fmtDate(ticket.created)}</div>;
    case "closeTime":   return <div className="tk-td col-updated" onClick={onClick}>{ticket.closedAt !== undefined ? fmtDate(ticket.closedAt) : <span style={{color:"var(--text-subtle)"}}>-</span>}</div>;
    case "solution":    {
      const sol = ticket.solution || "";
      return <div className="tk-td col-desc" onClick={onClick} title={sol}>{sol || <span style={{color:"var(--text-subtle)"}}>-</span>}</div>;
    }
    default: return <div className="tk-td" onClick={onClick}/>;
  }
};

// Compact numbered pagination. Shows first, current ± 2, last, with ellipses
// between non-adjacent runs. Never crowds - a 20-page dataset renders as
// [1] … [4] [5] [6] [7] [8] … [20] rather than every number.
const compactPageList = (current, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const around = new Set([1, totalPages, current - 1, current, current + 1]);
  // Always include neighbours-of-neighbours if there's room, keeps the run
  // around 'current' feeling substantial.
  if (current > 2) around.add(current - 2);
  if (current < totalPages - 1) around.add(current + 2);
  const nums = [...around].filter(n => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < nums.length; i++) {
    if (i > 0 && nums[i] - nums[i - 1] > 1) out.push('…');
    out.push(nums[i]);
  }
  return out;
};

const Pagination = ({ page, totalPages, showingLabel, onGoTo, disabled }) => {
  const btnStyle = (active) => ({
    minWidth: 28, height: 26, padding: '0 8px',
    border: '1px solid var(--line)',
    background: active ? 'var(--accent)' : 'transparent',
    color: active ? '#fff' : 'var(--text)',
    borderRadius: 4, cursor: disabled ? 'default' : 'pointer',
    fontSize: 12, fontFamily: 'inherit',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  });
  const gap = { padding: '0 4px', fontSize: 12, color: 'var(--text-subtle)', display: 'inline-flex', alignItems: 'center' };
  return (
    // Footer bar attached to the table - top border stitches it to the
    // scrollable list above so the pagination doesn't feel like a floating
    // island. "Showing X-Y of Z" sits on the left, controls on the right.
    <div style={{
      padding: '8px 14px',
      borderTop: '1px solid var(--line)',
      background: 'var(--bg-elev)',
      display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      justifyContent: 'space-between',
    }}>
      <div style={{ fontSize: 12, color: 'var(--text-subtle)' }}>{showingLabel}</div>
      {totalPages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <button style={btnStyle(false)} disabled={disabled || page <= 1} onClick={() => onGoTo(page - 1)}>‹</button>
          {compactPageList(page, totalPages).map((n, i) =>
            n === '…'
              ? <span key={`gap-${i}`} style={gap}>…</span>
              : <button key={n} style={btnStyle(n === page)} disabled={disabled} onClick={() => onGoTo(n)}>{n}</button>
          )}
          <button style={btnStyle(false)} disabled={disabled || page >= totalPages} onClick={() => onGoTo(page + 1)}>›</button>
        </div>
      )}
    </div>
  );
};

// True on phone-sized screens - swaps the wide ticket grid for compact cards.
const useIsPhone = () => {
  const q = '(max-width: 600px)';
  const [phone, setPhone] = React.useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(q).matches);
  React.useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(q);
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
};

// Compact phone ticket card - subject-first, status + priority, requester + time.
// Replaces the "LABEL: value" CSS-carded grid rows on phones (far less scrolling).
const MobileTicketCard = ({ ticket, onClick }) => {
  const r = getRequester(ticket.requester);
  const cat = getCategory(ticket.category)?.label;
  const threadCount = (ticket.messages || []).filter(m => m.from !== 'system').length;
  const attachmentCount = ticket.attachmentCount || 0;
  return (
    <div onClick={onClick} style={{
      background: 'var(--bg-elev)', border: '1px solid var(--line)',
      borderLeft: ticket.unread ? '3px solid var(--accent, #0d9488)' : '1px solid var(--line)',
      borderRadius: 10, padding: '11px 13px', cursor: 'pointer',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <StatusChip status={ticket.status}/>
        <PriorityChip priority={ticket.priority}/>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-subtle)', fontFamily: 'var(--mono)' }}>#{ticket.id}</span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.35,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {ticket.subject}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: 'var(--text-muted)', minWidth: 0 }}>
        <Avatar user={r} size="sm"/>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{r?.name || ticket.requester}</span>
        <span style={{ marginLeft: 'auto', flexShrink: 0, whiteSpace: 'nowrap', color: 'var(--text-subtle)' }}>{fmtDate(ticket.created)}</span>
      </div>
      {(cat || threadCount > 1 || attachmentCount > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          {cat && <span style={{ fontSize: 11, color: 'var(--text-subtle)' }}>{cat}</span>}
          {threadCount > 1 && <span style={{ fontSize: 10, fontWeight: 700, color: 'oklch(40% 0.12 264)', background: 'oklch(94% 0.04 264)', padding: '1px 6px', borderRadius: 3 }}>+{threadCount - 1}</span>}
          {attachmentCount > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10, color: 'var(--text-subtle)' }}><Icon name="paperclip" size={10}/>{attachmentCount}</span>}
        </div>
      )}
    </div>
  );
};

const TicketsView = ({ state, dispatch, onGoToPage }) => {
  const isPhone = useIsPhone();
  const { tickets, selectedId, selected, ticketsMeta, ticketsQuery } = state;
  const [dragCol, setDragCol] = React.useState(null);
  const [dropTarget, setDropTarget] = React.useState(null);

  // Server does the filtering, sorting and searching now - the current page
  // arrives pre-filtered in state.tickets. TicketsView just renders what
  // it's given. All the client-side useMemo filter/sort machinery from the
  // pre-pagination era is gone.
  const filtered = tickets;
  const sortBy   = ticketsQuery.sort || { col: 'requestTime', dir: 'desc' };

  const selectedTicket = selectedId ? tickets.find(t => t.id === selectedId) : null;

  // Deep-link fallback: the user landed on /tickets/{id} for a ticket that
  // isn't on the current page (e.g. followed a link from an email). Fetch
  // the full row by id and dispatch hydrateTicket - the reducer inserts it
  // into state.tickets, which triggers this render again and the detail
  // view mounts. Guard with a ref so we don't refire mid-fetch.
  const deepFetchRef = React.useRef(0);
  React.useEffect(() => {
    if (!selectedId || selectedTicket) return;
    if (deepFetchRef.current === selectedId) return;
    deepFetchRef.current = selectedId;
    window.IT_AUTH.api('GET', `/tickets/${selectedId}`, undefined, 'ticketDeepLink')
      .then(full => {
        if (full) dispatch({ type: 'hydrateTicket', ticket: full });
      })
      .catch(err => console.warn('[ticket deep link] failed:', err.message));
  }, [selectedId, selectedTicket, dispatch]);

  if (selectedTicket) {
    return (
      <TicketDetailFull
        ticket={selectedTicket}
        allTickets={tickets}
        view={state.view}
        dispatch={dispatch}
      />
    );
  }
  // Show a lightweight loading state while a deep-link fetch is in flight.
  if (selectedId && !selectedTicket) {
    return <div className="empty"><div><h3>Loading ticket…</h3></div></div>;
  }

  // Sort clicks dispatch to ticketsQuery so the server re-sorts on the
  // next fetch. The effect wired to ticketsQuery in App re-fires
  // automatically.
  const toggleSort = (col) => {
    const nextDir = sortBy.col === col ? (sortBy.dir === 'asc' ? 'desc' : 'asc') : 'desc';
    dispatch({ type: 'setTicketsQuery', patch: { sort: { col, dir: nextDir } } });
  };

  const allChecked = filtered.length > 0 && filtered.every(t => selected.has(t.id));
  const someChecked = filtered.some(t => selected.has(t.id));

  // Active visible columns + grid template
  const columns = state.columns;
  const gridTemplate = `32px ${columns.map(cid => {
    const meta = ALL_COLUMNS.find(c => c.id === cid);
    if (!meta) return "120px";
    return meta.flex ? `minmax(${meta.width}px, 2fr)` : `${meta.width}px`;
  }).join(" ")}`;
  const minWidth = 32 + columns.reduce((acc, cid) => acc + (ALL_COLUMNS.find(c => c.id === cid)?.width || 120), 0);

  // Drag-reorder state
  const onColDrop = (targetId) => {
    if (!dragCol || dragCol === targetId) { setDragCol(null); setDropTarget(null); return; }
    const next = columns.filter(c => c !== dragCol);
    const idx = next.indexOf(targetId);
    next.splice(idx, 0, dragCol);
    dispatch({type:"setColumns", columns: next});
    setDragCol(null);
    setDropTarget(null);
  };

  return (
    <div className="tk-table-wrap">
      <TableToolbar state={state} dispatch={dispatch} count={ticketsMeta.total}/>

      <div className="tk-table">
        {isPhone ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '10px 10px 28px' }}>
            {filtered.map(t => (
              <MobileTicketCard key={t.id} ticket={t} onClick={() => dispatch({ type: 'select', id: t.id })}/>
            ))}
          </div>
        ) : (
        <div className="tk-grid" style={{gridTemplateColumns: gridTemplate, minWidth}}>
          <div className="tk-th col-check">
            <button
              className={`chk ${allChecked ? "on" : someChecked ? "indet" : ""}`}
              onClick={() => { if (allChecked) dispatch({type:"clearChecks"}); else dispatch({type:"checkAll", ids: filtered.map(t => t.id)}); }}
            >
              {allChecked && <Icon name="check" size={10}/>}
            </button>
          </div>
          {columns.map(cid => {
            const meta = ALL_COLUMNS.find(c => c.id === cid);
            if (!meta) return null;
            return (
              <div
                key={cid}
                className={`tk-th ${sortBy.col === cid ? "sorted" : ""} ${dropTarget === cid ? "drop-target" : ""} ${dragCol === cid ? "dragging" : ""}`}
                draggable
                onDragStart={(e) => { setDragCol(cid); e.dataTransfer.effectAllowed = "move"; }}
                onDragEnd={() => { setDragCol(null); setDropTarget(null); }}
                onDragOver={(e) => { if (dragCol && dragCol !== cid) { e.preventDefault(); setDropTarget(cid); } }}
                onDragLeave={() => { if (dropTarget === cid) setDropTarget(null); }}
                onDrop={(e) => { e.preventDefault(); onColDrop(cid); }}
                onClick={() => toggleSort(cid)}
              >
                <Icon name="grip" size={10} style={{opacity: .35, marginRight: 2, cursor: "grab"}}/>
                {meta.label}
                <span className="sort-ind">{sortBy.col === cid ? (sortBy.dir === "asc" ? "↑" : "↓") : "↕"}</span>
              </div>
            );
          })}

          {filtered.map(t => (
            <TicketRow
              key={t.id}
              ticket={t}
              columns={columns}
              checked={selected.has(t.id)}
              onClick={() => dispatch({type:"select", id: t.id})}
              onCheck={() => dispatch({type:"toggleCheck", id: t.id})}
            />
          ))}
        </div>
        )}
        {filtered.length === 0 && !ticketsMeta.loading && (
          <div className="empty"><div><h3>All clear</h3><div>Nothing in this view.</div></div></div>
        )}
      </div>
      {ticketsMeta.total > 0 && (() => {
        // Pagination footer bar. Attached to the bottom of the table via a
        // top border - reads as part of the same UI block rather than a
        // floating widget below. Compact page controls plus a 'Showing X-Y
        // of Z' label so the reader always knows where they are.
        const totalPages = Math.max(1, Math.ceil(ticketsMeta.total / (ticketsMeta.size || 50)));
        const first = (ticketsMeta.page - 1) * (ticketsMeta.size || 50) + 1;
        const last  = Math.min(ticketsMeta.total, first + filtered.length - 1);
        return (
          <Pagination
            page={ticketsMeta.page || 1}
            totalPages={totalPages}
            showingLabel={`Showing ${first}–${last} of ${ticketsMeta.total}`}
            onGoTo={onGoToPage}
            disabled={ticketsMeta.loading}
          />
        );
      })()}
    </div>
  );
};

const TableToolbar = ({ state, dispatch, count }) => {
  const refs = { assign: React.useRef(), status: React.useRef(), cat: React.useRef(), req: React.useRef(), views: React.useRef() };
  const [pop, setPop] = React.useState(null);
  // Distinct requesters for the Requester filter picker (fetched once).
  const [requesters, setRequesters] = React.useState([]);
  const [reqSearch, setReqSearch] = React.useState('');
  React.useEffect(() => {
    window.IT_AUTH.api('GET', '/tickets/requesters', undefined, 'tkReq')
      .then(list => setRequesters(Array.isArray(list) ? list : []))
      .catch(() => setRequesters([]));
  }, []);

  const titles = { all: "All tickets", inbox: "All open", mine: "My tickets", unassigned: "Unassigned", resolved: "Closed", trash: "Recycle bin" };
  // Filter chip state lives on state.ticketsQuery now (single source of
  // truth - the same object the API request is built from). setFilter
  // helper dispatches into it and the fetch effect in App refires.
  const q = state.ticketsQuery || {};
  const setFilter = (patch) => dispatch({ type: 'setTicketsQuery', patch });
  const assigneeName = !q.assignee ? "Anyone" : q.assignee === "__none" ? "Unassigned" : (getUser(q.assignee)?.name?.split(" ")[0] || "Anyone");
  const statusLabel = q.status === 'open' ? "Open" : q.status ? STATUSES.find(s=>s.id===q.status)?.label : "Any status";
  const catLabel = q.category ? getCategory(q.category)?.label : "All categories";
  const requesterLabel = q.requester ? (requesters.find(r => r.email === q.requester)?.name || q.requester) : "Anyone";
  const currentView = BUILTIN_VIEWS.find(v => v.id === state.tableView);
  const viewLabel = currentView ? currentView.name : "Custom view";

  const toggleColumn = (cid) => {
    const meta = ALL_COLUMNS.find(c => c.id === cid);
    if (meta?.required) return;
    if (state.columns.includes(cid)) {
      dispatch({type:"setColumns", columns: state.columns.filter(c => c !== cid)});
    } else {
      dispatch({type:"setColumns", columns: [...state.columns, cid]});
    }
  };

  return (
    <div className="tk-toolbar2">
      <span style={{fontWeight:600, fontSize:13}}>{titles[state.view] || "Tickets"}</span>
      <span style={{fontFamily:"var(--mono)", fontSize:11, color:"var(--text-subtle)"}}>{count}</span>

      <span style={{width:1, height:18, background:"var(--line)", margin:"0 4px"}}/>

      {/* The Mine view hard-pins assignee to ME_ID - exposing the assignee
          filter there would mislead (it'd say "Anyone" while the view shows
          only mine) and any other pick would silently return 0 results. */}
      {state.view !== "mine" && (
        <button ref={refs.assign} className={`tk-filter ${q.assignee ? "on" : ""}`} onClick={() => setPop(pop === "assign" ? null : "assign")}>
          <Icon name="user" size={11}/> Assignee: {assigneeName}
          <Icon name="chevron" size={10}/>
        </button>
      )}

      {/* On the 'Closed' view the status filter is redundant - everything
          shown is already resolved. Hidden entirely there. Elsewhere it
          appears with only the statuses that make sense (see popover
          below): 'All open' + 'Unassigned' drop the Closed option;
          'All tickets' / 'Mine' / 'Trash' show all three. */}
      {state.view !== "resolved" && (
        <button ref={refs.status} className={`tk-filter ${q.status ? "on" : ""}`} onClick={() => setPop(pop === "status" ? null : "status")}>
          <Icon name="flag" size={11}/> {statusLabel}
          <Icon name="chevron" size={10}/>
        </button>
      )}

      <button ref={refs.cat} className={`tk-filter ${q.category ? "on" : ""}`} onClick={() => setPop(pop === "cat" ? null : "cat")}>
        <Icon name="tag" size={11}/> {catLabel}
        <Icon name="chevron" size={10}/>
      </button>

      <button ref={refs.req} className={`tk-filter ${q.requester ? "on" : ""}`} onClick={() => { setReqSearch(''); setPop(pop === "req" ? null : "req"); }}>
        <Icon name="mail" size={11}/> Requester: {requesterLabel}
        <Icon name="chevron" size={10}/>
      </button>

      {(q.assignee || q.status || q.category || q.requester) && (
        <button className="tk-filter" onClick={() => setFilter({ assignee: null, status: null, category: null, requester: null })}>
          <Icon name="close" size={10}/> Clear
        </button>
      )}

      <div className="spacer"/>

      <button ref={refs.views} className="tk-filter" onClick={() => setPop(pop === "views" ? null : "views")}>
        <Icon name="layers" size={11}/> View: {viewLabel}
        <Icon name="chevron" size={10}/>
      </button>

      {pop === "assign" && (
        <Popover anchorRef={refs.assign} onClose={() => setPop(null)}>
          <div className="pop-item" onClick={() => { setFilter({ assignee: null }); setPop(null); }}>Anyone</div>
          <div className="pop-item" onClick={() => { setFilter({ assignee: "__none" }); setPop(null); }}>
            <span style={{width:18}}/> Unassigned
          </div>
          <div className="pop-divider"/>
          {USERS.map(u => (
            <div key={u.id} className="pop-item" onClick={() => { setFilter({ assignee: u.id }); setPop(null); }}>
              <Avatar user={u} size="sm"/> {u.name}
              {u.id === ME_ID && <span className="kbd">me</span>}
            </div>
          ))}
        </Popover>
      )}
      {pop === "status" && (() => {
        // Options depend on the view - no point offering statuses the view
        // already excludes. Open + Unassigned exclude 'resolved'; the rest
        // show everything. The 'Any status' entry is always available so
        // users can clear a filter they've set.
        const openOnly = state.view === "inbox" || state.view === "unassigned";
        const statusOptions = openOnly ? STATUSES.filter(s => s.id !== "resolved") : STATUSES;
        // "Open (New + In Progress)" combined option - the value 'open' maps
        // server-side to status <> resolved. Redundant on inbox/unassigned
        // (already open-only), so only offered where Closed is otherwise
        // visible. This is the option My Tickets defaults to.
        const showOpenOption = !openOnly;
        return (
          <Popover anchorRef={refs.status} onClose={() => setPop(null)}>
            <div className="pop-item" onClick={() => { setFilter({ status: null }); setPop(null); }}>Any status</div>
            {showOpenOption && (
              <div className="pop-item" onClick={() => { setFilter({ status: 'open' }); setPop(null); }}>
                <span className="dot prog"/> Open (New + In Progress)
              </div>
            )}
            {statusOptions.map(s => (
              <div key={s.id} className="pop-item" onClick={() => { setFilter({ status: s.id }); setPop(null); }}>
                <span className={`dot ${s.tone}`}/> {s.label}
              </div>
            ))}
          </Popover>
        );
      })()}
      {pop === "cat" && (
        <Popover anchorRef={refs.cat} onClose={() => setPop(null)}>
          <div className="pop-item" onClick={() => { setFilter({ category: null }); setPop(null); }}>All categories</div>
          {CATEGORIES.map(c => (
            <div key={c.id} className="pop-item" onClick={() => { setFilter({ category: c.id }); setPop(null); }}>
              {c.label}
            </div>
          ))}
        </Popover>
      )}
      {pop === "req" && (
        <Popover anchorRef={refs.req} onClose={() => setPop(null)}>
          <div style={{ padding: '6px 8px' }}>
            <input
              autoFocus value={reqSearch} onChange={e => setReqSearch(e.target.value)}
              placeholder="Search requester…"
              style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '5px 8px', border: '1px solid var(--line)', borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text)', fontFamily: 'inherit' }}
            />
          </div>
          <div className="pop-item" onClick={() => { setFilter({ requester: null }); setPop(null); }}>Anyone</div>
          <div className="pop-divider"/>
          <div style={{ maxHeight: 280, overflowY: 'auto' }}>
            {(() => {
              const s = reqSearch.trim().toLowerCase();
              const list = requesters.filter(r => !s || (r.name || '').toLowerCase().includes(s) || (r.email || '').toLowerCase().includes(s));
              if (list.length === 0) return <div className="pop-item" style={{ color: 'var(--text-subtle)' }}>{requesters.length ? 'No match' : 'Loading…'}</div>;
              return list.slice(0, 200).map(r => (
                <div key={r.email} className="pop-item" onClick={() => { setFilter({ requester: r.email }); setPop(null); }} title={r.email}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  <span className="kbd" style={{ marginLeft: 'auto' }}>{r.n}</span>
                </div>
              ));
            })()}
          </div>
        </Popover>
      )}
      {pop === "views" && (
        <Popover anchorRef={refs.views} onClose={() => setPop(null)} align="right">
          <div style={{padding:"6px 8px 4px", fontSize:10, color:"var(--text-subtle)", letterSpacing:".05em", textTransform:"uppercase"}}>Saved views</div>
          {BUILTIN_VIEWS.map(v => (
            <div key={v.id} className="pop-item" onClick={() => { dispatch({type:"setView", viewId: v.id}); setPop(null); }}>
              <Icon name="layers" size={11}/> {v.name}
              <span style={{marginLeft:"auto", fontFamily:"var(--mono)", fontSize:10, color:"var(--text-subtle)"}}>{v.columns.length} cols</span>
              {state.tableView === v.id && <Icon name="check" size={11} style={{marginLeft:6}}/>}
            </div>
          ))}
          <div className="pop-divider"/>
          <div style={{padding:"6px 8px 4px", fontSize:10, color:"var(--text-subtle)", letterSpacing:".05em", textTransform:"uppercase"}}>Columns</div>
          {ALL_COLUMNS.map(c => {
            const on = state.columns.includes(c.id);
            return (
              <div
                key={c.id}
                className="pop-item"
                onClick={(e) => { e.stopPropagation(); toggleColumn(c.id); }}
                style={{opacity: c.required ? 0.6 : 1, cursor: c.required ? "default" : "pointer"}}
              >
                <Icon name={on ? "eye" : "eyeOff"} size={11}/>
                <span>{c.label}</span>
                {c.required && <span style={{marginLeft:"auto", fontSize:10, color:"var(--text-subtle)"}}>required</span>}
              </div>
            );
          })}
          <div className="pop-divider"/>
          <div style={{padding: "6px 10px", fontSize: 10, color: "var(--text-subtle)"}}>
            Tip: drag column headers to reorder.
          </div>
        </Popover>
      )}
    </div>
  );
};

// Inline-renameable ticket subject (detail header). Pencil -> input;
// Enter/blur saves, Escape cancels, empty reverts. Persists via the
// renameTicket action (PATCH /tickets/{id} { subject }).
const EditableSubject = ({ ticket, dispatch }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(ticket.subject);
  React.useEffect(() => { setDraft(ticket.subject); setEditing(false); }, [ticket.id, ticket.subject]);
  const save = () => {
    const v = draft.trim();
    setEditing(false);
    if (!v || v === ticket.subject) { setDraft(ticket.subject); return; }
    dispatch({ type: "renameTicket", id: ticket.id, subject: v });
  };
  if (!editing) return (
    <h1 style={{display:"flex", alignItems:"center", gap:8, minWidth:0}}>
      <span style={{overflow:"hidden", textOverflow:"ellipsis"}}>{ticket.subject}</span>
      <button title="Rename subject" onClick={() => setEditing(true)}
        style={{background:"none", border:"none", cursor:"pointer", color:"var(--text-subtle)", padding:2, lineHeight:1, flexShrink:0}}>
        <Icon name="edit" size={13}/>
      </button>
    </h1>
  );
  return (
    <h1 style={{margin:0}}>
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") { setDraft(ticket.subject); setEditing(false); } }}
        style={{font:"inherit", width:"100%", border:"1px solid var(--accent)", borderRadius:6,
                padding:"2px 8px", background:"var(--bg-elev)", color:"var(--text)", outline:"none"}}
      />
    </h1>
  );
};

const TicketRow = ({ ticket, columns, checked, onClick, onCheck }) => {
  // Highlight tickets whose workflow status is still "new" - i.e. nobody
  // has progressed them yet. Persists even after the ticket has been
  // opened/read, so it survives a "took a look but didn't action" pass.
  // Flips off the moment someone moves it to In Progress / Resolved.
  const isNew = ticket.status === "new";
  return (
    <div className={`tk-tr row ${checked ? "selected" : ""} ${isNew ? "is-new" : ""}`}>
      <div className="tk-td col-check" onClick={(e) => e.stopPropagation()}>
        <button className={`chk ${checked ? "on" : ""}`} onClick={onCheck}>
          {checked && <Icon name="check" size={10}/>}
        </button>
      </div>
      {columns.map(cid => {
        // Stamp each cell with its column label as data-label. Desktop ignores
        // it (harmless attribute); the mobile card layout surfaces it via a
        // CSS ::before so each stacked cell reads "Assignee: Jane" etc. cloning
        // avoids touching every case in renderCell.
        const label = ALL_COLUMNS.find(c => c.id === cid)?.label || "";
        return React.cloneElement(renderCell(cid, ticket, onClick), { key: cid, "data-label": label });
      })}
    </div>
  );
};

// ---------------- Ticket Detail (full page) ----------------

const TicketDetailFull = ({ ticket, allTickets, view, dispatch }) => {
  const titles = { all: "All tickets", inbox: "All open", mine: "My tickets", unassigned: "Unassigned", resolved: "Closed", trash: "Recycle bin" };
  // Hydrate the full ticket (with follow-up message bodies) when the detail
  // view opens. The paginated list endpoint returns slim rows - first
  // message body only - so opening any ticket beyond page 1 or any ticket
  // with a follow-up thread needs the full row here. Same pattern KB uses.
  // Re-runs whenever ticket.id changes so navigating between tickets in the
  // detail view hydrates each one in turn.
  React.useEffect(() => {
    const msgs = Array.isArray(ticket.messages) ? ticket.messages : [];
    // Determine if we already have every follow-up body. If any non-first
    // message has an empty body, we need to hydrate.
    const needsHydration = msgs.length > 1 && msgs.slice(1).some(m => !m.body);
    if (!needsHydration) return;
    let cancelled = false;
    window.IT_AUTH.api('GET', `/tickets/${ticket.id}`, undefined, 'ticketHydrate')
      .then(full => {
        if (cancelled || !full) return;
        dispatch({ type: 'hydrateTicket', ticket: full });
      })
      .catch(err => console.warn('[ticket hydrate] failed:', err.message));
    return () => { cancelled = true; };
  }, [ticket.id]);

  return (
    <div className="tk-detail-full">
      <div className="tk-back">
        <button className="btn ghost" onClick={() => dispatch({type:"select", id: null})}>
          ← Back
        </button>
        <div className="breadcrumb">
          <span>{titles[view] || "Tickets"}</span>
          <span className="sep">/</span>
          <b>#{ticket.id}</b>
          <span className="sep">·</span>
          <span style={{color:"var(--text)"}}>{ticket.subject}</span>
        </div>
        <div style={{flex:1}}/>
        <button className="btn ghost"><Icon name="link" size={11}/> Copy link</button>
        {ticket.trashed
          ? <button className="btn ghost" style={{color:"var(--st-done)"}} onClick={() => dispatch({type:"restoreTicket", id: ticket.id})}>
              <Icon name="refresh" size={11}/> Restore
            </button>
          : <button className="btn ghost" style={{color:"oklch(50% 0.15 25)"}} onClick={() => dispatch({type:"trashTicket", id: ticket.id})}>
              <Icon name="trash" size={11}/> Delete
            </button>
        }
      </div>

      <TicketBody key={ticket.id} ticket={ticket} allTickets={allTickets} dispatch={dispatch}/>
    </div>
  );
};

// Attachment row + upload panel. Self-contained - fetches its own list on
// mount and on ticket change. Lives in its own component so it can manage
// its loading state without entangling TicketBody.
const ATTACHMENT_MIME_BY_EXT = {
  png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif',
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc: 'application/msword', xls: 'application/vnd.ms-excel', ppt: 'application/vnd.ms-powerpoint',
  txt: 'text/plain', csv: 'text/csv',
};
const ICON_BY_EXT = (name) => {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['png','jpg','jpeg','webp','gif'].includes(ext)) return '🖼️';
  if (ext === 'pdf') return '📄';
  if (['doc','docx'].includes(ext)) return '📝';
  if (['xls','xlsx','csv'].includes(ext)) return '📊';
  if (['ppt','pptx'].includes(ext)) return '📽️';
  return '📎';
};
const fmtBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentsBlock = ({ ticket }) => {
  const ticketId = ticket.id;
  // Optimistic creates set updatedAt to undefined (the client-side reducer
  // builds the row from form fields and doesn't fake a server timestamp).
  // Real tickets always have it once the API response merges in. Used below
  // to skip reload + uploads during the brief optimistic window.
  const isPersisted = !!ticket.updatedAt;
  const [items, setItems] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef();

  const reload = React.useCallback(() => {
    if (!isPersisted) { setItems([]); return; }
    window.IT_AUTH.api('GET', `/tickets/${ticketId}/attachments`, undefined, 'attachments')
      .then(list => setItems(Array.isArray(list) ? list : []))
      .catch(e => { console.warn('[attachments]', e.message); setItems([]); });
  }, [ticketId, isPersisted]);

  // Re-fetch when the ticket transitions from optimistic to persisted (and
  // on any ID change). Without the isPersisted dep, the initial mount would
  // miss the moment the real ticket arrives.
  React.useEffect(() => { reload(); }, [reload]);

  // Convert a File → { mimeType, data:base64, filename } then POST to upload.
  // Done one file at a time so the size-cap and magic-byte checks fire per
  // file with clear error messages.
  const uploadOne = async (file) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    const declared = file.type || ATTACHMENT_MIME_BY_EXT[ext] || '';
    if (!declared) {
      setError(`Unsupported file: ${file.name}`);
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      setError(`${file.name} is too large (max 25 MB)`);
      return;
    }
    const buf = await file.arrayBuffer();
    // Browser-friendly base64 - chunked to avoid 'Maximum call stack' on big files.
    const bytes = new Uint8Array(buf);
    let bin = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    const b64 = btoa(bin);
    try {
      await window.IT_AUTH.api('POST', `/tickets/${ticketId}/attachments`, {
        mimeType: declared,
        data: b64,
        filename: file.name,
      }, 'attachmentUpload');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleFiles = async (fileList) => {
    console.log('[attach] handleFiles called', { count: fileList?.length, ticketId, isPersisted });
    if (!fileList || !fileList.length) return;
    if (!isPersisted) {
      setError('Still saving the ticket - try again in a second.');
      return;
    }
    setError(null);
    setUploading(true);
    try {
      for (const f of fileList) {
        console.log('[attach] uploading', f.name, f.type, f.size);
        await uploadOne(f);
      }
      reload();
    } catch (e) {
      console.error('[attach] upload threw', e);
      setError(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const remove = async (att) => {
    if (!window.confirm(`Remove ${att.fileName}?`)) return;
    try {
      await window.IT_AUTH.api('DELETE', `/tickets/${ticketId}/attachments/${att.id}`, undefined, 'attachmentDelete');
      reload();
    } catch (e) { setError(e.message); }
  };

  // Download via authenticated fetch (not a plain <a href>) so the bearer
  // token is sent. Stream the blob into an object URL and trigger a
  // download - saves the file as the original name, not the UUID.
  const download = async (att) => {
    try {
      const token = await window.IT_AUTH.getApiToken();
      const apiBase = 'https://it-ticketing-api-dcgwgjerducxg8ck.uksouth-01.azurewebsites.net/api';
      const res = await fetch(`${apiBase}/tickets/${ticketId}/attachments/${att.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Download failed: ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="tk-card" style={{marginBottom:16}}>
      <div className="tk-card-label" style={{display:"flex", alignItems:"center", gap:8}}>
        <Icon name="paperclip" size={12}/>
        Attachments
        {items != null && <span style={{fontSize:11, color:"var(--text-subtle)"}}>· {items.length}</span>}
      </div>

      {items === null ? (
        <div style={{fontSize:12, color:"var(--text-subtle)", padding:"6px 0"}}>Loading…</div>
      ) : (
        <>
          {items.length > 0 && (
            <div style={{display:"flex", flexDirection:"column", gap:6, marginBottom:items.length ? 10 : 0}}>
              {items.map(att => (
                <div key={att.id} style={{
                  display:"flex", alignItems:"center", gap:10,
                  padding:"8px 10px", border:"1px solid var(--line)", borderRadius:6,
                  background:"var(--bg-elev)",
                }}>
                  <span style={{fontSize:18, lineHeight:1, flexShrink:0}}>{ICON_BY_EXT(att.fileName)}</span>
                  <div style={{flex:1, minWidth:0}}>
                    <div style={{fontSize:13, fontWeight:500, color:"var(--text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                      {att.fileName}
                    </div>
                    <div style={{fontSize:11, color:"var(--text-subtle)", marginTop:1}}>
                      {fmtBytes(att.size)}
                      {att.source === 'email' && " · from email"}
                      {att.uploadedBy && att.source === 'manual' && ` · by ${att.uploadedBy.split('@')[0]}`}
                    </div>
                  </div>
                  <button
                    onClick={() => download(att)}
                    style={{background:"none", border:"1px solid var(--line)", borderRadius:5, padding:"4px 10px", fontSize:12, cursor:"pointer", color:"var(--text)"}}
                  >
                    Download
                  </button>
                  <button
                    onClick={() => remove(att)}
                    title="Delete attachment"
                    style={{background:"none", border:"none", padding:"4px 6px", cursor:"pointer", color:"var(--text-subtle)", fontSize:15, lineHeight:1}}
                  >×</button>
                </div>
              ))}
            </div>
          )}

          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => {
              console.log('[attach] dropzone clicked, ref=', !!fileInputRef.current);
              fileInputRef.current?.click();
            }}
            style={{
              padding:"14px 12px", textAlign:"center",
              border:`1.5px dashed ${dragOver ? 'var(--accent)' : 'var(--line)'}`,
              background: dragOver ? 'var(--bg-sub)' : 'transparent',
              borderRadius:6, cursor: uploading ? 'wait' : 'pointer',
              fontSize:12, color:"var(--text-subtle)",
              transition:'background 120ms ease, border-color 120ms ease',
            }}
          >
            {uploading
              ? 'Uploading…'
              : <>Drag &amp; drop files here, or click to browse. <span style={{color:"var(--text-subtle)"}}>Max 25 MB each. PDF, images, Office docs, text/CSV.</span></>
            }
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{display:"none"}}
              onChange={e => {
                handleFiles(e.target.files);
                // Reset so re-selecting the same file fires onChange again.
                // Without this, "upload File A → upload File A again" silently
                // does nothing because the input's value hasn't changed.
                e.target.value = '';
              }}
            />
          </div>

          {error && (
            <div style={{fontSize:12, color:"#B91C1C", marginTop:8, padding:"6px 10px", background:"#FEF2F2", border:"1px solid #FECACA", borderRadius:5}}>
              {error}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Compliance ticket layout - shown instead of the generic reply thread
// when a ticket was auto-created from a User Request (File Access /
// Information Barrier). Renders the same structured fields the SysAid
// tickets used, plus the WSM audit panel from requests.jsx. Read-only:
// the source of truth is the UserRequests row, not the ticket.
const ComplianceTicketBody = ({ ticket }) => {
  const r = ticket.userRequest;
  const isBarrier = r && r.type === 'info-barrier';
  const peopleLabel = isBarrier ? 'Side 1 - User Access Granted To' : 'Access Given To';
  const partner = r && r.matterPartner
    ? (r.matterPartner.displayName || r.matterPartner.email)
    : '-';
  const people = (r && Array.isArray(r.accessGrantedTo) && r.accessGrantedTo.length)
    ? r.accessGrantedTo.map(p => p.displayName || p.email).join(', ')
    : '-';
  const clientLabel = r ? `${r.clientNumber}${r.clientDescription ? ` (${r.clientDescription})` : ''}` : '-';
  const matterLabel = r ? `${r.matterNumber}${r.matterDescription ? ` (${r.matterDescription})` : ''}` : '-';
  const outcomeLabel = r ? (
    r.status === 'completed'    ? 'Completed - applied in NetDocuments'
    : r.status === 'completed-na' ? 'Completed - not applicable (no change made)'
    : r.status === 'rejected'   ? 'Rejected'
    : r.status
  ) : '-';

  const cellLabel = { display: 'block', fontSize: 11, color: 'var(--text-subtle)', marginBottom: 4, letterSpacing: 0.3, textTransform: 'uppercase' };
  const cellValue = { display: 'block', fontSize: 13, color: 'var(--text)', whiteSpace: 'pre-wrap' };
  const cell      = { padding: '12px 0' };

  return (
    <div className="tk-d-body" style={{ padding: '20px 24px', overflowY: 'auto' }}>
      <div className="tk-card" style={{ padding: '16px 20px', marginBottom: 16 }}>
        <div className="tk-card-label" style={{ marginBottom: 12 }}>General Details</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
          <div style={cell}>
            <span style={cellLabel}>Client Number</span>
            <span style={cellValue}>{clientLabel}</span>
          </div>
          <div style={cell}>
            <span style={cellLabel}>Matter Number</span>
            <span style={cellValue}>{matterLabel}</span>
          </div>
          <div style={cell}>
            <span style={cellLabel}>Matter Partner</span>
            <span style={cellValue}>{partner}</span>
          </div>
          <div style={cell}>
            <span style={cellLabel}>Approved By</span>
            <span style={cellValue}>{r && (r.reviewedByName || r.reviewedByEmail) || '-'}</span>
          </div>
          <div style={{ ...cell, gridColumn: '1 / -1' }}>
            <span style={cellLabel}>{peopleLabel}</span>
            <span style={cellValue}>{people}</span>
          </div>
          {r && r.reason && (
            <div style={{ ...cell, gridColumn: '1 / -1' }}>
              <span style={cellLabel}>Reason</span>
              <span style={cellValue}>{r.reason}</span>
            </div>
          )}
          <div style={cell}>
            <span style={cellLabel}>Outcome</span>
            <span style={cellValue}>{outcomeLabel}</span>
          </div>
          <div style={cell}>
            <span style={cellLabel}>Requested By</span>
            <span style={cellValue}>{r && (r.requesterName || r.requesterEmail) || '-'}</span>
          </div>
          {r && r.decisionNote && (
            <div style={{ ...cell, gridColumn: '1 / -1' }}>
              <span style={cellLabel}>Decision Note</span>
              <span style={cellValue}>{r.decisionNote}</span>
            </div>
          )}
        </div>
      </div>

      {r && r.wsmApplyResult && <WsmApplyDetail apply={r.wsmApplyResult}/>}
    </div>
  );
};

const TicketBody = ({ ticket, allTickets, dispatch }) => {
  const [solutionText, setSolutionText] = React.useState(ticket.solution || "");
  const [catPop, setCatPop] = React.useState(null);
  const [showCatWarn, setShowCatWarn] = React.useState(false);
  // Thread expand/collapse. Lives at the top level (not inside the conditional
  // IIFE below) because Hooks must be called in the same order every render -
  // putting it inside a `{length > 1 && (() => { useState(...) })()}` block
  // violates the Rules of Hooks and silently breaks the thread section.
  // Defaults to OPEN because a collapsed thread is too easy to miss when you
  // open a ticket fresh and don't realise the conversation has follow-ups.
  // Thread starts collapsed - after moving Solution above it, the thread
  // is context you drill into rather than the primary content. Users who
  // want to see the follow-ups can expand with one click; users who don't
  // (most, most of the time) skip past a big wall of quoted email chains.
  const [threadOpen, setThreadOpen] = React.useState(false);
  const catRef = React.useRef();
  const subcatRef = React.useRef();
  const locRef = React.useRef();
  const pendingCloseRef = React.useRef(null);

  React.useEffect(() => {
    setSolutionText(ticket.solution || "");
  }, [ticket.id]);

  const requester = getRequester(ticket.requester);
  const assignee = ticket.assignee ? getUser(ticket.assignee) : null;
  const description = decodeEntities(ticket.messages[0]?.body || "");

  // Duplicate-merge suggestions. Earlier version matched on subject alone,
  // which produced wildly wrong suggestions (e.g. a week-old unrelated ticket
  // with the same generic title). Now requires:
  //   1. Same subject (case + whitespace insensitive)
  //   2. Same requester (otherwise it's a different person's issue)
  //   3. Within 14 days of each other (older tickets aren't follow-ups)
  //   4. Not already resolved (don't merge into closed tickets)
  //   5. Not already merged elsewhere (no double-merging chains)
  const MERGE_WINDOW_MIN = 14 * 24 * 60; // 14 days in minutes (created is mins-ago)
  const duplicates = allTickets.filter(t => {
    if (t.id === ticket.id) return false;
    if (t.duplicateOf) return false;
    if (t.status === 'resolved') return false;
    if (t.subject.trim().toLowerCase() !== ticket.subject.trim().toLowerCase()) return false;
    if ((t.requester || '').toLowerCase() !== (ticket.requester || '').toLowerCase()) return false;
    // Both `created` fields are minutes-ago (negative). Diff is absolute mins.
    const diffMins = Math.abs((t.created || 0) - (ticket.created || 0));
    if (diffMins > MERGE_WINDOW_MIN) return false;
    return true;
  });

  // TODO: KB_ARTICLES is now an empty [] in data.jsx (real KB lives in SQL via
  // /api/kb). For "suggested KB articles in the ticket sidebar" to work again,
  // wire state.kbArticles down to TicketBody via props and read from there.
  // Empty filter is a temporary safe no-op until that plumbing is done.
  const suggestedKB = KB_ARTICLES.filter(a =>
    a.category === ticket.category &&
    (a.tags.some(tag => ticket.tags?.includes(tag)) || a.title.toLowerCase().includes(ticket.subcat.toLowerCase().split(" ")[0]))
  ).slice(0, 3);

  const tryClose = (action) => {
    if (!ticket.category) {
      pendingCloseRef.current = action;
      setShowCatWarn(true);
      return;
    }
    action();
  };

  const handleClose = () => tryClose(() => dispatch({type:"resolve", id: ticket.id, solution: solutionText}));

  // Compliance ticket swap - if this ticket was auto-created from a
  // File Access / Info Barrier request, render the compliance-shaped
  // form + WSM audit panel instead of the reply thread. Header row is
  // kept so id/status/priority chips still show above.
  if (ticket.userRequest) {
    return (
      <>
        <div className="tk-d-head">
          <div className="row1">
            <span className="id">#{ticket.id}</span>
            <span style={{color:"var(--text-subtle)"}}>·</span>
            <StatusChip status={ticket.status}/>
            <PriorityChip priority={ticket.priority}/>
            <div style={{flex:1}}/>
            <span style={{fontSize: 12, color: 'var(--text-subtle)'}}>
              Auto-generated from user request #{ticket.userRequestId}
            </span>
          </div>
          <div className="row2">
            <div className="tk-subject">{ticket.subject}</div>
          </div>
        </div>
        <ComplianceTicketBody ticket={ticket}/>
      </>
    );
  }

  return (
    <>
      <div className="tk-d-head">
        <div className="row1">
          <span className="id">#{ticket.id}</span>
          <span style={{color:"var(--text-subtle)"}}>·</span>
          <StatusChip status={ticket.status}/>
          <PriorityChip priority={ticket.priority}/>
          <div style={{flex:1}}/>
          <button
            ref={catRef}
            className="tk-filter"
            style={!ticket.category ? {borderColor:"oklch(72% 0.10 55)",color:"oklch(48% 0.10 55)"} : {}}
            onClick={() => setCatPop(p => p === "cat" ? null : "cat")}
          >
            <Icon name="tag" size={11}/>
            {ticket.category ? getCategory(ticket.category)?.label : "No category"}
            <Icon name="chevron" size={10}/>
          </button>
          <button
            ref={subcatRef}
            className="tk-filter"
            onClick={() => { if (ticket.category) setCatPop(p => p === "subcat" ? null : "subcat"); }}
            style={!ticket.category ? {opacity:0.4,cursor:"not-allowed"} : {}}
            title={!ticket.category ? "Set a category first" : undefined}
          >
            {ticket.subcat || <span style={{color:"var(--text-subtle)"}}>Subcategory</span>}
            <Icon name="chevron" size={10}/>
          </button>
          {catPop === "cat" && (
            <Popover anchorRef={catRef} onClose={() => setCatPop(null)}>
              {CATEGORIES.map(c => (
                <div key={c.id} className="pop-item" onClick={() => { dispatch({type:"setCategory", id: ticket.id, category: c.id}); setCatPop(null); }}>
                  {c.label}
                  {ticket.category === c.id && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
                </div>
              ))}
            </Popover>
          )}
          {catPop === "subcat" && ticket.category && (
            <Popover anchorRef={subcatRef} onClose={() => setCatPop(null)}>
              {(SUBCATS[ticket.category] || []).map(s => (
                <div key={s} className="pop-item" onClick={() => { dispatch({type:"setSubcat", id: ticket.id, subcat: s}); setCatPop(null); }}>
                  {s}
                  {ticket.subcat === s && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
                </div>
              ))}
            </Popover>
          )}
          <button
            ref={locRef}
            className="tk-filter"
            style={!ticket.location ? {borderColor:"oklch(72% 0.10 55)",color:"oklch(48% 0.10 55)"} : {}}
            onClick={() => setCatPop(p => p === "loc" ? null : "loc")}
          >
            <Icon name="flag" size={11}/>
            {ticket.location || "No location"}
            <Icon name="chevron" size={10}/>
          </button>
          {catPop === "loc" && (
            <Popover anchorRef={locRef} onClose={() => setCatPop(null)}>
              <div style={{maxHeight:260, overflowY:"auto"}}>
                {(LOCATIONS || []).map(l => (
                  <div key={l} className="pop-item" onClick={() => { dispatch({type:"setLocation", id: ticket.id, location: l}); setCatPop(null); }}>
                    {l}
                    {ticket.location === l && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
                  </div>
                ))}
              </div>
            </Popover>
          )}
          {!ticket.assignee && (
            <button className="btn primary" onClick={() => dispatch({type:"assign", id: ticket.id, assignee: ME_ID})}>
              <Avatar user={getUser(ME_ID)} size="sm"/> Assign to me
            </button>
          )}
          {ticket.status !== "resolved" && (
            <button className="btn" onClick={handleClose}>
              <Icon name="check" size={11}/> Mark closed
            </button>
          )}
        </div>
        <EditableSubject ticket={ticket} dispatch={dispatch}/>
        <div className="tk-d-meta">
          <Avatar user={requester} size="sm"/>
          <span style={{fontSize:12, color:"var(--text-muted)"}}>
            From <strong style={{color:"var(--text)"}}>{requester?.name}</strong>
            {requester?.dept ? ` · ${requester.dept}` : ""}
            {" · "}{fmtDate(ticket.created)}
          </span>
        </div>
      </div>

      <div className="tk-d-body">
        <div className="tk-d-main">
          {duplicates.length > 0 && (
            <div className="merge-banner">
              <Icon name="merge" size={14}/>
              {duplicates.length} ticket{duplicates.length > 1 ? "s" : ""} with same subject.
              <button style={{color:"oklch(35% 0.10 70)", textDecoration:"underline", marginLeft:"auto"}} onClick={() => dispatch({type:"mergeDuplicates", primaryId: ticket.id, sourceIds: duplicates.map(d => d.id)})}>Merge into this</button>
            </div>
          )}

          {suggestedKB.length > 0 && !ticket.solution && (
            <div className="tk-card" style={{marginBottom:16}}>
              <div className="tk-card-label"><Icon name="sparkle" size={11}/> Suggested articles</div>
              {suggestedKB.map(a => (
                <div key={a.id} onClick={() => dispatch({type:"openKB", articleId: a.id})} style={{display:"flex", alignItems:"center", gap:8, padding:"7px 0", cursor:"pointer", borderTop:"1px solid var(--line)"}}>
                  <Icon name="book" size={12}/>
                  <span style={{fontWeight:500, fontSize:13}}>{a.title}</span>
                  <span style={{marginLeft:"auto", fontFamily:"var(--mono)", fontSize:11, color:"var(--text-subtle)"}}>KB-{a.id}</span>
                </div>
              ))}
            </div>
          )}

          <div className="tk-card">
            <div className="tk-card-label">Description</div>
            <TruncatedText text={description} emptyLabel="No description provided."/>
          </div>

          {/* Solution - moved above the thread so it's reachable on long
              email threads without a giant scroll. Writing a resolution or
              re-reading one on a closed ticket is the primary action on
              this page; the thread is context, the solution is the answer. */}
          <div className="tk-card tk-solution-card">
            <div className="tk-card-label" style={{display:"flex", alignItems:"center", gap:8}}>
              <Icon name="check" size={12}/>
              Solution
              {ticket.status === "resolved" && <span style={{fontSize:10, fontWeight:600, padding:"1px 7px", borderRadius:10, background:"var(--st-closed-bg)", color:"var(--st-closed)", marginLeft:4}}>CLOSED</span>}
            </div>
            <textarea
              className="tk-solution-input"
              value={solutionText}
              onChange={e => setSolutionText(e.target.value)}
              placeholder="Record what fixed the issue - what was done, any steps taken, useful for KB articles…"
              readOnly={ticket.status === "resolved"}
            />
            {ticket.status !== "resolved" && (
              <>
                {showCatWarn && (
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",marginTop:10,background:"oklch(98% 0.015 65)",border:"1px solid oklch(82% 0.07 65)",borderRadius:"var(--radius-sm)"}}>
                    <Icon name="tag" size={13} style={{color:"oklch(52% 0.12 65)",flexShrink:0}}/>
                    <span style={{fontSize:12,color:"oklch(38% 0.08 65)",flex:1}}>No category set - worth adding to help track ticket trends.</span>
                    <button className="btn" style={{fontSize:11,padding:"3px 10px"}} onClick={() => { setShowCatWarn(false); setCatPop("cat"); }}>Add category</button>
                    <button className="btn" style={{fontSize:11,padding:"3px 10px"}} onClick={() => { pendingCloseRef.current?.(); pendingCloseRef.current = null; setShowCatWarn(false); }}>Close anyway</button>
                    <button onClick={() => { setShowCatWarn(false); pendingCloseRef.current = null; }} style={{background:"none",border:"none",cursor:"pointer",color:"var(--text-subtle)",fontSize:18,lineHeight:1,padding:"0 2px"}}>×</button>
                  </div>
                )}
                <div style={{display:"flex", gap:8, marginTop:10, flexWrap:"wrap"}}>
                  <button className="btn primary" disabled={!solutionText.trim()} onClick={handleClose}>
                    <Icon name="check" size={11}/> Save &amp; close
                  </button>
                  {ticket.assignee !== ME_ID && (
                    <button className="btn" disabled={!solutionText.trim()} onClick={() => tryClose(() => dispatch({type:"assignAndResolve", id: ticket.id, assignee: ME_ID, solution: solutionText}))}>
                      <Avatar user={getUser(ME_ID)} size="sm"/> Assign to me &amp; close
                    </button>
                  )}
                  <button className="btn" onClick={() => dispatch({type:"saveSolution", id: ticket.id, solution: solutionText})}>
                    Save draft
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Add Note - Rob's feature request 17 Jun 2026. Lets IT log an
              update against a pending ticket without needing to email the
              requester. Note gets appended to the thread as an internal
              message (isInternal=true) so it renders with the 'Internal
              note' tag and stays out of any future public-facing view.
              Placed between Solution and Thread so it's always near the
              top of the page - same reasoning as Solution's position. */}
          <AddNoteBlock ticket={ticket} dispatch={dispatch}/>

          {/* Thread of follow-up messages. The first message renders as the
              Description above; everything after that is a "follow-up" and
              lives here.

              Previous version used a <button> with padding:0 on the parent
              + overflow:hidden - browser default button styles collided with
              the parent's flex layout and collapsed the card to 2px tall.
              Now uses a plain div with onClick, default card padding, and an
              explicit border-top separator for the messages section. */}
          {(() => {
            // Split the follow-ups two ways:
            //   Notes   - IT-added internal messages (isInternal + non-system)
            //   Thread  - external emails from the requester (non-internal),
            //             plus system markers like 'Merged from #X' that
            //             have always lived in the thread.
            // Rendered as two separate cards. Notes usually short and IT-
            // relevant → default expanded. Thread often long email chains
            // → default collapsed. Both toggleable.
            const followUps = Array.isArray(ticket.messages) ? ticket.messages.slice(1) : [];
            const notes  = followUps.filter(m => m.internal && m.from !== 'system' && !String(m.body || '').startsWith('__'));
            const thread = followUps.filter(m => !notes.includes(m));
            return (
              <>
                {notes.length > 0 && (
                  <div className="tk-card" style={{marginBottom: 16}}>
                    <div className="tk-card-label" style={{display: 'flex', alignItems: 'center', gap: 8}}>
                      <Icon name="edit" size={11}/>
                      Notes - {notes.length} internal note{notes.length !== 1 ? 's' : ''}
                    </div>
                    <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 10}}>
                      {notes.map((msg, i) => (
                        <NoteRow
                          key={msg.id || `n${i}`}
                          msg={msg}
                          requester={requester}
                          ticketId={ticket.id}
                          dispatch={dispatch}
                        />
                      ))}
                    </div>
                  </div>
                )}
                {thread.length > 0 && (
                  <div className="tk-card" style={{marginBottom: 16}}>
                    <div
                      onClick={() => setThreadOpen(o => !o)}
                      role="button"
                      style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none'}}
                    >
                      <span className="tk-card-label" style={{flex: 1, margin: 0}}>
                        Thread - {thread.length} follow-up{thread.length !== 1 ? 's' : ''}
                      </span>
                      <Icon name="chevron" size={11} style={{transform: threadOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', opacity: 0.5}}/>
                    </div>
                    {threadOpen && (
                      <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 10}}>
                        {thread.map((msg, i) => (
                          <Message key={msg.id || `m${i}`} msg={msg} requester={requester} onSplit={
                            msg.id && msg.from !== 'system'
                              ? () => { if (window.confirm('Split this message into a new ticket?')) dispatch({type:'splitMessage', ticketId: ticket.id, messageId: msg.id}); }
                              : null
                          }/>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            );
          })()}

          <AttachmentsBlock ticket={ticket}/>
        </div>

        <TicketRail ticket={ticket} requester={requester} assignee={assignee} dispatch={dispatch}/>
      </div>
    </>
  );
};

// Text renderer that hides everything past ~600 chars behind a 'Show more'
// button. Threshold chosen so a normal short email body renders in full;
// only the long forwarded-thread bodies (with quoted history) get folded.
// Preserves whitespace + wrapping (whiteSpace: pre-wrap). Deliberately
// doesn't touch HTML - inputs here are plain text.
const TruncatedText = ({ text, limit = 600, emptyLabel = '' }) => {
  const [expanded, setExpanded] = React.useState(false);
  const s = String(text || '');
  if (!s) {
    return <div className="tk-desc-body" style={{color:"var(--text-subtle)", fontStyle:"italic"}}>{emptyLabel}</div>;
  }
  const long = s.length > limit;
  const shown = long && !expanded ? s.slice(0, limit).trimEnd() + '…' : s;
  return (
    <>
      <div className="tk-desc-body" style={{ whiteSpace: 'pre-wrap' }}>{shown}</div>
      {long && (
        <button
          onClick={() => setExpanded(v => !v)}
          style={{
            marginTop: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600,
            background: 'transparent', border: '1px solid var(--line)',
            borderRadius: 4, cursor: 'pointer',
            color: 'var(--accent-text, var(--accent))',
            fontFamily: 'inherit',
          }}
        >
          {expanded ? 'Show less' : `Show more (${(s.length - limit).toLocaleString()} more chars)`}
        </button>
      )}
    </>
  );
};

// Compact 'Add note' card. Starts as a collapsed pill; clicking expands
// into a textarea + Save button. Notes save as internal messages
// (isInternal=true) on the ticket, so they appear in the Thread section
// with the same 'Internal note' treatment existing internal messages get.
// The backend's addMessage handler auto-transitions status=new → progress
// on any message add, matching the "log an update to a pending ticket"
// intent - you don't have to also flip the status pill.
const AddNoteBlock = ({ ticket, dispatch }) => {
  const [open, setOpen] = React.useState(false);
  const [text, setText] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const textareaRef = React.useRef(null);

  // Focus the textarea when we expand. Avoids the user having to click
  // twice (once to open, once to focus).
  React.useEffect(() => {
    if (open && textareaRef.current) textareaRef.current.focus();
  }, [open]);

  const save = () => {
    const body = text.trim();
    if (!body || saving) return;
    setSaving(true);
    // Optimistic dispatch - the reducer appends this to the thread
    // immediately; the syncAction fires the POST /messages call.
    const me = getUser(ME_ID);
    dispatch({
      type: 'addMessage',
      id: ticket.id,
      msg: {
        from: me?.email || ME_ID,
        body,
        internal: true,
        sentAt: new Date().toISOString(),
      },
    });
    // Reset + collapse. Note appears in the dedicated Notes section
    // below (isInternal + non-system messages get split out from the
    // email Thread), which defaults expanded so it's immediately visible.
    setText('');
    setOpen(false);
    setSaving(false);
  };

  return (
    <div className="tk-card" style={{ marginBottom: 16 }}>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, width: '100%',
            padding: '8px 4px', background: 'transparent', border: 'none',
            cursor: 'pointer', color: 'var(--text-muted)', fontSize: 13,
            fontFamily: 'inherit', textAlign: 'left',
          }}
        >
          <Icon name="edit" size={12}/>
          <span>Add an internal note</span>
        </button>
      ) : (
        <>
          <div className="tk-card-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="edit" size={12}/>
            Internal note
            <span style={{
              fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10,
              background: 'var(--bg-sub)', color: 'var(--text-muted)', marginLeft: 4,
              letterSpacing: 0.3, textTransform: 'uppercase',
            }}>IT team only</span>
          </div>
          <textarea
            ref={textareaRef}
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
              if (e.key === 'Escape') { setText(''); setOpen(false); }
            }}
            placeholder="e.g. Waiting on procurement to raise the PO - will chase Friday."
            style={{
              width: '100%', minHeight: 90, padding: '10px 12px',
              border: '1px solid var(--line)', borderRadius: 6,
              fontFamily: 'inherit', fontSize: 13, resize: 'vertical',
              outline: 'none', boxSizing: 'border-box',
              background: 'var(--bg)', color: 'var(--text)',
            }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <button
              className="btn primary"
              disabled={!text.trim() || saving}
              onClick={save}
            >
              <Icon name="check" size={11}/> Add note
            </button>
            <button
              className="btn"
              onClick={() => { setText(''); setOpen(false); }}
            >Cancel</button>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-subtle)' }}>
              Ctrl + Enter to save
            </span>
          </div>
        </>
      )}
    </div>
  );
};

// Editable / deletable version of the message row for internal notes.
// Reuses Message's rendering for the read state; when the pencil is
// clicked, swaps in a textarea to edit. Confirmed delete removes the row
// (optimistic; server DELETE fires via the syncAction). Only rendered
// inside the Notes card - external emails still render as plain Message.
const NoteRow = ({ msg, requester, ticketId, dispatch }) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(msg.body || '');
  React.useEffect(() => { setDraft(msg.body || ''); }, [msg.body]);

  const save = () => {
    const body = draft.trim();
    if (!body || body === (msg.body || '').trim()) { setEditing(false); return; }
    dispatch({ type: 'editNote', ticketId, messageId: msg.id, body });
    setEditing(false);
  };
  const del = () => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    dispatch({ type: 'deleteNote', ticketId, messageId: msg.id });
  };

  if (editing) {
    return (
      <div className="tk-msg internal" style={{ padding: 12 }}>
        <textarea
          value={draft}
          autoFocus
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
            if (e.key === 'Escape') { setDraft(msg.body || ''); setEditing(false); }
          }}
          style={{
            width: '100%', minHeight: 80, padding: 8, fontFamily: 'inherit',
            fontSize: 13, border: '1px solid var(--line)', borderRadius: 4,
            background: 'var(--bg-elev)', color: 'var(--text)',
            resize: 'vertical', outline: 'none', boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <button className="btn primary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={save} disabled={!draft.trim()}>
            Save
          </button>
          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setDraft(msg.body || ''); setEditing(false); }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}
         onMouseEnter={e => e.currentTarget.querySelector('.note-actions').style.opacity = '1'}
         onMouseLeave={e => e.currentTarget.querySelector('.note-actions').style.opacity = '0'}>
      <Message msg={msg} requester={requester} onSplit={null}/>
      {/* Only allow edit/delete on saved notes - new/unsaved notes have no
          id yet (server hasn't responded), so the API call would 404. */}
      {msg.id && (
        <div
          className="note-actions"
          style={{
            position: 'absolute', top: 8, right: 8,
            display: 'flex', gap: 4,
            opacity: 0, transition: 'opacity 120ms',
          }}
        >
          <button
            onClick={() => setEditing(true)}
            title="Edit note"
            style={{
              padding: '3px 8px', fontSize: 11, background: 'var(--bg-elev)',
              border: '1px solid var(--line)', borderRadius: 4,
              color: 'var(--text-muted)', cursor: 'pointer',
            }}
          >Edit</button>
          <button
            onClick={del}
            title="Delete note"
            style={{
              padding: '3px 8px', fontSize: 11, background: 'var(--bg-elev)',
              border: '1px solid var(--line)', borderRadius: 4,
              color: '#EF4444', cursor: 'pointer',
            }}
          >Delete</button>
        </div>
      )}
    </div>
  );
};

const Message = ({ msg, requester, onSplit }) => {
  const [hovered, setHovered] = React.useState(false);

  // System dividers
  if (msg.from === 'system') {
    let icon = "merge", text = "";
    if (msg.body?.startsWith('__merged_from__'))  text = `Merged from ticket ${msg.body.replace('__merged_from__', '')}`;
    else if (msg.body?.startsWith('__split_to__')) text = `Split to ticket ${msg.body.replace('__split_to__', '')}`;
    else if (msg.body?.startsWith('__split_from__')) text = `Split from ticket ${msg.body.replace('__split_from__', '')}`;
    else return null;
    return (
      <div style={{display:"flex", alignItems:"center", gap:10, padding:"10px 0", color:"var(--text-subtle)", fontSize:12}}>
        <div style={{flex:1, height:1, background:"var(--line)"}}/>
        <span style={{display:"flex", alignItems:"center", gap:5, whiteSpace:"nowrap"}}>
          <Icon name={icon} size={11}/> {text}
        </span>
        <div style={{flex:1, height:1, background:"var(--line)"}}/>
      </div>
    );
  }

  // Match either the USERS.id (short slug like 'james' - legacy path for
  // some system-authored messages) OR the email (what the backend stores
  // as fromEmail and what internal notes send). Without the email match,
  // an IT-added note falls through to `requester` and shows attributed to
  // the ticket's requester instead of the person who wrote it.
  const fromLc = String(msg.from || '').toLowerCase();
  const isFromTeam = USERS.find(u =>
    u.id === msg.from || (u.email || '').toLowerCase() === fromLc
  );
  const senderUser = isFromTeam || requester;
  return (
    <div
      className={`tk-msg ${msg.internal ? "internal" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{border:"1px solid var(--line)", borderRadius:"var(--radius-sm)", background:"var(--bg-elev)", overflow:"hidden"}}
    >
      <div className="tk-msg-head" style={{display:"flex", alignItems:"center", gap:8, padding:"8px 12px", background:"var(--bg-sub)", borderBottom:"1px solid var(--line)", fontSize:12, color:"var(--text-muted)"}}>
        <Avatar user={senderUser} size="sm"/>
        <span className="tk-msg-from">{senderUser?.name}</span>
        {!isFromTeam && <span className="tk-msg-to">to triage@leeandthompson.com</span>}
        {msg.internal && <span className="tk-msg-tag">Internal note</span>}
        {/* Prefer the ISO sentAt from the server - it's the exact wall-clock
            time and avoids the client-vs-server clock drift that made this
            display go into tomorrow's date. `msg.time` is legacy fallback
            for messages already in-flight before the API sent sentAt. */}
        <span className="tk-msg-time">{fmtDate(msg.sentAt || msg.time)}</span>
        {hovered && onSplit && (
          <button
            onClick={onSplit}
            title="Split into separate ticket"
            style={{marginLeft:8, padding:"2px 8px", fontSize:11, background:"none", border:"1px solid var(--line)", borderRadius:"var(--radius-sm)", cursor:"pointer", color:"var(--text-muted)", display:"flex", alignItems:"center", gap:4}}
          >
            <Icon name="merge" size={10}/> Unmerge
          </button>
        )}
      </div>
      <div className="tk-msg-body" style={{whiteSpace:"pre-wrap", padding:"12px 14px"}}>{decodeEntities(msg.body)}</div>
    </div>
  );
};

const TicketRail = ({ ticket, requester, assignee, dispatch }) => {
  const [popover, setPopover] = React.useState(null);
  const [requesterQuery, setRequesterQuery] = React.useState("");
  const refs = {
    assign: React.useRef(), status: React.useRef(),
    priority: React.useRef(), category: React.useRef(),
    subcat: React.useRef(), requester: React.useRef(),
  };

  return (
    <div className="tk-rail">
      <div className="rail-section">
        <div className="rail-label">Assignee</div>
        <div className="rail-row" ref={refs.assign} onClick={() => setPopover("assign")}>
          {assignee
            ? <><Avatar user={assignee} size="sm"/><span className="label">{assignee.name}</span></>
            : <span className="label" style={{color:"var(--text-subtle)"}}>Unassigned</span>
          }
          <span className="val muted"><Icon name="chevron" size={10}/></span>
        </div>
        {!assignee && (
          <button className="btn" style={{marginTop:6, width:"100%", justifyContent:"center"}} onClick={() => dispatch({type:"assign", id: ticket.id, assignee: ME_ID})}>
            <Avatar user={getUser(ME_ID)} size="sm"/> Assign to me
          </button>
        )}
      </div>

      <div className="rail-section">
        <div className="rail-label">Status</div>
        <div className="rail-row" ref={refs.status} onClick={() => setPopover("status")}>
          <span className={`dot ${STATUSES.find(s=>s.id===ticket.status)?.tone}`}/>
          <span className="label">{STATUSES.find(s=>s.id===ticket.status)?.label}</span>
          <span className="val muted"><Icon name="chevron" size={10}/></span>
        </div>
      </div>

      <div className="rail-section">
        <div className="rail-label">Priority</div>
        <div className="rail-row" ref={refs.priority} onClick={() => setPopover("priority")}>
          <span className={`pri-bar ${ticket.priority}`}/>
          <span className="label">{PRIORITIES.find(p=>p.id===ticket.priority)?.label}</span>
          <span className="val muted"><Icon name="chevron" size={10}/></span>
        </div>
      </div>

      <div className="rail-section">
        <div className="rail-label">Category</div>
        <div className="rail-row" ref={refs.category} onClick={() => setPopover("category")}>
          <span className="label" style={{fontSize:12}}>{getCategory(ticket.category)?.label}</span>
          <span className="val muted"><Icon name="chevron" size={10}/></span>
        </div>
        <div className="rail-label" style={{marginTop:8}}>Subcategory</div>
        <div className="rail-row" ref={refs.subcat} onClick={() => setPopover("subcat")}>
          <span className="label" style={{fontSize:12}}>{ticket.subcat || <span style={{color:"var(--text-subtle)"}}>-</span>}</span>
          <span className="val muted"><Icon name="chevron" size={10}/></span>
        </div>
      </div>

      <div className="rail-section">
        <div className="rail-label">Requester</div>
        <div className="rail-row" ref={refs.requester} onClick={() => { setRequesterQuery(""); setPopover("requester"); }} style={{cursor:"pointer"}}>
          <Avatar user={requester} size="sm"/>
          <div style={{minWidth:0}}>
            <div style={{fontSize:12, fontWeight:500}}>{requester?.name || <span style={{color:"var(--text-subtle)"}}>No requester</span>}</div>
            <div style={{fontSize:11, color:"var(--text-subtle)", textOverflow:"ellipsis", overflow:"hidden"}}>{requester?.email}</div>
          </div>
          <span className="val muted"><Icon name="chevron" size={10}/></span>
        </div>
        {requester?.dept && (
          <div style={{padding:"4px 6px 0", fontSize:11, color:"var(--text-subtle)"}}>
            {requester.dept} · <span style={{fontFamily:"var(--mono)"}}>M365 ↗</span>
          </div>
        )}
      </div>

      <div className="rail-section">
        <div className="rail-label">Tags</div>
        <div style={{display:"flex", flexWrap:"wrap", gap:4}}>
          {(ticket.tags || []).map(t => (
            <span key={t} className="tag" style={{cursor:"default"}}>{t}</span>
          ))}
          <button className="tag" style={{borderStyle:"dashed"}}>+ add</button>
        </div>
      </div>

      <div className="rail-section">
        <div className="rail-label">Activity</div>
        <div style={{fontSize:11, color:"var(--text-subtle)", lineHeight: 1.9}}>
          <div><span style={{color:"var(--text-muted)", marginRight:4}}>Requested</span>{fmtDate(ticket.created)}</div>
          {ticket.closedAt !== undefined && <div><span style={{color:"var(--text-muted)", marginRight:4}}>Closed</span>{fmtDate(ticket.closedAt)}</div>}
          <div>{ticket.messages.length} {ticket.messages.length === 1 ? "message" : "messages"}</div>
        </div>
      </div>

      {popover === "assign" && (
        <Popover anchorRef={refs.assign} onClose={() => setPopover(null)}>
          <div className="pop-item" onClick={() => { dispatch({type:"assign", id: ticket.id, assignee: null}); setPopover(null); }}>
            <span className="av sm empty">·</span> Unassigned
          </div>
          <div className="pop-divider"/>
          {USERS.map(u => (
            <div key={u.id} className="pop-item" onClick={() => { dispatch({type:"assign", id: ticket.id, assignee: u.id}); setPopover(null); }}>
              <Avatar user={u} size="sm"/> {u.name}
              {u.id === ME_ID && <span className="kbd">me</span>}
              {ticket.assignee === u.id && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
            </div>
          ))}
        </Popover>
      )}
      {popover === "status" && (
        <Popover anchorRef={refs.status} onClose={() => setPopover(null)}>
          {STATUSES.map(s => (
            <div key={s.id} className="pop-item" onClick={() => { dispatch({type:"setStatus", id: ticket.id, status: s.id}); setPopover(null); }}>
              <span className={`dot ${s.tone}`}/> {s.label}
              {ticket.status === s.id && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
            </div>
          ))}
        </Popover>
      )}
      {popover === "priority" && (
        <Popover anchorRef={refs.priority} onClose={() => setPopover(null)}>
          {PRIORITIES.map(p => (
            <div key={p.id} className="pop-item" onClick={() => { dispatch({type:"setPriority", id: ticket.id, priority: p.id}); setPopover(null); }}>
              <span className={`pri-bar ${p.id}`}/> {p.label}
              {ticket.priority === p.id && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
            </div>
          ))}
        </Popover>
      )}
      {popover === "category" && (
        <Popover anchorRef={refs.category} onClose={() => setPopover(null)}>
          {CATEGORIES.map(c => (
            <div key={c.id} className="pop-item" onClick={() => { dispatch({type:"setCategory", id: ticket.id, category: c.id}); setPopover(null); }}>
              {c.label}
              {ticket.category === c.id && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
            </div>
          ))}
        </Popover>
      )}
      {popover === "subcat" && (
        <Popover anchorRef={refs.subcat} onClose={() => setPopover(null)}>
          {(SUBCATS[ticket.category] || []).map(s => (
            <div key={s} className="pop-item" onClick={() => { dispatch({type:"setSubcat", id: ticket.id, subcat: s}); setPopover(null); }}>
              {s}
              {ticket.subcat === s && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
            </div>
          ))}
        </Popover>
      )}
      {popover === "requester" && (
        <Popover anchorRef={refs.requester} onClose={() => setPopover(null)} align="right">
          <div style={{padding:"8px 8px 6px", borderBottom:"1px solid var(--line)"}}>
            <input
              autoFocus
              value={requesterQuery}
              onChange={e => setRequesterQuery(e.target.value)}
              placeholder="Search staff…"
              style={{width:"100%", padding:"5px 8px", border:"1px solid var(--line)", borderRadius:"var(--radius-sm)", fontSize:12, background:"var(--bg)", color:"var(--text)", outline:"none"}}
            />
          </div>
          <div style={{maxHeight:220, overflowY:"auto"}}>
            {(window.STAFF_LIST || [])
              .filter(s => !requesterQuery || s.displayName.toLowerCase().includes(requesterQuery.toLowerCase()))
              .map(s => (
                <div key={s.mail} className="pop-item" onClick={() => { dispatch({type:"setRequester", id: ticket.id, requester: s.mail, requesterName: s.displayName}); setPopover(null); }}>
                  <Avatar user={{ name: s.displayName, mail: s.mail, email: s.mail, color: "#888", initials: s.displayName.split(" ").filter(Boolean).map(n=>n[0]).join("").slice(0,2).toUpperCase() }} size="sm"/>
                  <div style={{minWidth:0}}>
                    <div style={{fontSize:12}}>{s.displayName}</div>
                    {s.department && <div style={{fontSize:11, color:"var(--text-subtle)"}}>{s.department}</div>}
                  </div>
                  {ticket.requester === s.mail && <Icon name="check" size={11} style={{marginLeft:"auto"}}/>}
                </div>
              ))
            }
          </div>
        </Popover>
      )}
    </div>
  );
};

export { TicketsView, TicketRow };
